import { unpackQueuePayload, type QueuePayloadFraming } from '@pink/shared';

import { base64ToBytes } from '../pinning-module/base64-to-bytes.js';
import { CryptoError, CryptoErrorKind } from './crypto-error.js';

/**
 * Klartext-Antwort des Tenants nach erfolgreichem Polling.
 *
 * - `SUCCESS`: Absage angenommen, `reason` bleibt leer.
 * - `REJECTED`: Absage abgelehnt, `reason` kann einen freien Text enthalten.
 */
export interface TenantResponsePayload {
  readonly status: 'SUCCESS' | 'REJECTED';
  readonly reason?: string;
}

const IV_BYTE_LENGTH = 12;
const UNWRAP_ALGORITHM: RsaOaepParams = { name: 'RSA-OAEP' };
const AES_PARAMS: AesKeyAlgorithm = { name: 'AES-GCM', length: 256 };

/**
 * Entschlüsselt einen Queue-Wire-Payload (`payload`-Feld der Spec, Base64-
 * verpacktes `QueuePayloadFraming`-Triple) mit dem nicht-extrahierbaren
 * Patient-Privatkey und parst das Ergebnis als `TenantResponsePayload`.
 *
 * Fehlerklassen:
 * - Unpack, Base64-Decode, IV-Längen-Check, RSA-Unwrap oder AES-Decrypt
 *   schlagen fehl → `CryptoError({ kind: DECRYPT_FAILED })`.
 * - JSON-Parse oder Schema-Guard schlägt fehl → `CryptoError({ kind:
 *   INVALID_FORMAT })`.
 *
 * @param payloadWire Wire-Payload-String (Base64, vom Queue-Server geliefert).
 * @param patientPrivateKey Nicht-extrahierbarer Privatkey aus IDB.
 */
export async function decryptResponse(
  payloadWire: string,
  patientPrivateKey: CryptoKey,
): Promise<TenantResponsePayload> {
  const framing = unpackOrThrow(payloadWire);
  const parts = decodeWireParts(framing);
  const aesKey = await unwrapAesKey(parts.encryptedKey, patientPrivateKey);
  const plaintextBytes = await decryptCiphertext(parts.iv, parts.ciphertext, aesKey);
  return parsePayload(plaintextBytes);
}

interface WireParts {
  readonly ciphertext: Uint8Array;
  readonly encryptedKey: Uint8Array;
  readonly iv: Uint8Array;
}

function unpackOrThrow(payloadWire: string): QueuePayloadFraming {
  try {
    return unpackQueuePayload(payloadWire);
  } catch (error) {
    throw new CryptoError({ kind: CryptoErrorKind.DecryptFailed, cause: error });
  }
}

function decodeWireParts(framing: QueuePayloadFraming): WireParts {
  try {
    const iv = base64ToBytes(framing.iv);
    if (iv.byteLength !== IV_BYTE_LENGTH) {
      throw new CryptoError({ kind: CryptoErrorKind.DecryptFailed });
    }
    return {
      ciphertext: base64ToBytes(framing.ciphertext),
      encryptedKey: base64ToBytes(framing.encryptedKey),
      iv,
    };
  } catch (error) {
    if (error instanceof CryptoError) {
      throw error;
    }
    throw new CryptoError({ kind: CryptoErrorKind.DecryptFailed, cause: error });
  }
}

async function unwrapAesKey(
  encryptedKey: Uint8Array,
  patientPrivateKey: CryptoKey,
): Promise<CryptoKey> {
  try {
    return await crypto.subtle.unwrapKey(
      'raw',
      encryptedKey,
      patientPrivateKey,
      UNWRAP_ALGORITHM,
      AES_PARAMS,
      false,
      ['decrypt'],
    );
  } catch (error) {
    throw new CryptoError({ kind: CryptoErrorKind.DecryptFailed, cause: error });
  }
}

async function decryptCiphertext(
  iv: Uint8Array,
  ciphertext: Uint8Array,
  aesKey: CryptoKey,
): Promise<Uint8Array> {
  try {
    const buffer = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, aesKey, ciphertext);
    return new Uint8Array(buffer);
  } catch (error) {
    throw new CryptoError({ kind: CryptoErrorKind.DecryptFailed, cause: error });
  }
}

function parsePayload(plaintextBytes: Uint8Array): TenantResponsePayload {
  let parsed: unknown;
  try {
    const text = new TextDecoder().decode(plaintextBytes);
    parsed = JSON.parse(text);
  } catch (error) {
    throw new CryptoError({ kind: CryptoErrorKind.InvalidFormat, cause: error });
  }
  if (!isTenantResponsePayload(parsed)) {
    throw new CryptoError({ kind: CryptoErrorKind.InvalidFormat });
  }
  return parsed;
}

function isTenantResponsePayload(value: unknown): value is TenantResponsePayload {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const record = value as Record<string, unknown>;
  if (record.status !== 'SUCCESS' && record.status !== 'REJECTED') {
    return false;
  }
  if (record.reason !== undefined && typeof record.reason !== 'string') {
    return false;
  }
  return true;
}
